import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import { AuthUIProviderTanstack } from "@daveyplate/better-auth-ui/tanstack";
import type { QueryClient as ReactQueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AdminLayout } from "@/components/admin/admin-layout";
import { ThemeProvider } from "@/components/common/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { useAdminAuth } from "@/hooks/use-admin-auth";
import { authClient } from "@/lib/auth-client";
import type { orpc } from "@/utils/orpc";

export interface RouterAppContext {
	orpc: typeof orpc;
	queryClient: ReactQueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
	component: RootComponent,
});

function RootComponent() {
	const { t } = useTranslation();
	const { isAdmin, isLoading } = useAdminAuth();

	if (isLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-center">
					<p className="text-muted-foreground">{t("common:loading")}</p>
				</div>
			</div>
		);
	}

	if (!isAdmin) {
		return (
			<ThemeProvider
				attribute="class"
				defaultTheme="light"
				disableTransitionOnChange
				storageKey="admin-ui-theme"
			>
				<div className="flex h-screen items-center justify-center">
					<div className="text-center">
						<h1 className="font-bold text-2xl">
							{t("admin:access_denied.title")}
						</h1>
						<p className="text-muted-foreground">
							{t("admin:access_denied.message")}
						</p>
						<a
							href={`${import.meta.env.VITE_WEB_APP_URL || ""}/auth/sign-in`}
							className="mt-4 inline-block text-primary hover:underline"
						>
							{t("admin:access_denied.go_to_sign_in")}
						</a>
					</div>
				</div>
				<Toaster />
			</ThemeProvider>
		);
	}

	return (
		<AuthQueryProvider>
			<AuthUIProviderTanstack authClient={authClient}>
				<ThemeProvider
					attribute="class"
					defaultTheme="light"
					disableTransitionOnChange
					storageKey="admin-ui-theme"
				>
					<AdminLayout>
						<Outlet />
					</AdminLayout>
					<Toaster />
				</ThemeProvider>
			</AuthUIProviderTanstack>
		</AuthQueryProvider>
	);
}
